import { useNavigate, useParams } from 'react-router-dom'

import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import {
  CreateTrainingClassMutationVariables,
  UpdateCostsClassDetailDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateTrainingClass } from 'modules/trainings/hooks/TrainingClasses/useCreateTrainingClass'

import { AttachmentsStep } from './AttachmentsStep'
import { BasicInformationStep } from './BasicInformationStep'
import { CostsStep } from './CostsStep'
import { EmployeeSelectorStep } from './EmployeeSelectorStep'
import { EventDataStep } from './EventDataStep'
import { ClassWizardData } from './hooks/useNewClassWizardSteps'

const NewClassWizard = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { access, company } = useCurrentSession()
  const { has_dev_trainings: devTrainings, loadingFeatures } = useFeatures(['dev_trainings'])

  const [linkBudget] = useMutation(UpdateCostsClassDetailDocument, {
    extractorFn: (data) => data.trainingsMutations.updateTrainingsTrainingClass,
  })

  const initialData: ClassWizardData = {
    trainingId: String(id),
    startDate: '',
    endDate: '',
    companyId: company.id,
    name: '',
    mode: 'scheduled',
    authorId: access.id,
    trainingSessions: [],
    attachments: [],
    employeesSelection: {
      type: 'manual',
      ids: [],
    },
    cost: '',
    indirectCost: '',
    salaryCost: '',
    subsidizedCost: '',
    codigoGrupo: '',
    budgetId: null,
  }

  const { create } = useCreateTrainingClass()
  const handleSubmit = async (data: ClassWizardData) => {
    data.trainingSessions.forEach((session) => {
      session.sessionInstructors = {
        employeeSelection: session.sessionInstructors.employeeSelection ?? {
          type: 'manual',
          ids: [],
        },
        sessionId: session.id ? session.id.toString() : undefined,
        companyId: company.id,
      }
    })
    const { codigoGrupo, attachments, budgetId, ...rest } = data
    const payload: CreateTrainingClassMutationVariables = {
      ...rest,
      selection: rest.employeesSelection,
      attachments: attachments.map((att) => {
        if (att.signedId) {
          return {
            authorId: att.authorId,
            signedId: att.signedId,
            title: att.title,
          }
        }
        return {
          file: att.file,
          title: att.title,
          authorId: att.authorId,
        }
      }),
    }
    if (codigoGrupo) {
      payload.fundaeConfiguration = {
        codigoGrupo,
      }
    }

    const result = await create(payload)

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('trainings.trainings_revamp.training_class.class_wizard.error_message'),
      })
    })

    if (handled.success) {
      const createdClassId = handled.data.trainingClass?.id

      // Link budget to the class if budgetId was selected
      if (budgetId && createdClassId) {
        const linkResult = await linkBudget({
          variables: {
            id: createdClassId,
            budgetId,
          },
        })

        const linkHandled = handleResult(linkResult, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('trainings.trainings_revamp.training_class.class_wizard.error_message'),
          })
        })

        if (!linkHandled.success) {
          return
        }
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.trainings_revamp.training_class.class_wizard.success_message'),
      })

      navigate(
        navigation.trainings.list.details_revamp.view.trainingClasses.tab.fullPath({
          id: String(id),
        })
      )
    }
  }

  const basicInformationStep: WizardStep<ClassWizardData> = {
    title: i18n.t(
      'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.reviewed_copies.sidebar_title'
    ),
    nextText: i18n.t('common.next'),
    render: (props) => <BasicInformationStep {...props} />,
    isCompleted: (data) => {
      return Boolean(data.name) && Boolean(data.startDate) && Boolean(data.endDate)
    },
  }

  const eventStep: WizardStep<ClassWizardData> = {
    title: i18n.t(
      'trainings.trainings_revamp.training_class.class_wizard.event_data_step.sidebar_title'
    ),
    nextText: i18n.t('common.next'),
    render: (props) => <EventDataStep {...props} featureFlag={devTrainings} />,
    isCompleted: (_data) => {
      return true
    },
  }

  const employeesStep: WizardStep<ClassWizardData> = {
    title: i18n.t('common.employees'),
    nextText: i18n.t('common.next'),
    render: (props) => <EmployeeSelectorStep {...props} />,
    isCompleted: () => true,
  }

  const costsStep: WizardStep<ClassWizardData> = {
    title: i18n.t('trainings.trainings_revamp.training_class.class_wizard.costs_step.title'),
    nextText: i18n.t('common.next'),
    render: (props) => <CostsStep {...props} />,
    isCompleted: () => true,
  }

  const attachmentsStep: WizardStep<ClassWizardData> = {
    title: i18n.t(
      'trainings.trainings_revamp.training_class.class_wizard.attachments_step.attachments'
    ),
    nextText: i18n.t('common.save'),
    render: (props) => <AttachmentsStep {...props} />,
    isCompleted: () => true,
  }

  const onClose = () =>
    navigate(
      navigation.trainings.list.details_revamp.view.trainingClasses.tab.fullPath({
        id: String(id),
      })
    )

  if (loadingFeatures) return <Wizard.LoadingModal onClose={onClose} />

  return (
    <Wizard.Modal
      title={i18n.t('trainings.trainings_revamp.training_class.class_wizard.title')}
      steps={[basicInformationStep, eventStep, employeesStep, costsStep, attachmentsStep]}
      initialData={initialData}
      onSubmit={(data) => handleSubmit(data)}
      onClose={onClose}
      size="large"
    />
  )
}

export default NewClassWizard
